import React, { useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Link, Navigate, useLocation, useParams,
} from 'react-router-dom';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { useTheme } from '../../contexts/ThemeContext';
import { useInMarketingShell } from '../Marketing/MarketingContext';
import {
  usePodcastRegistry, getEpisode, relatedEpisodes, formatDuration, parseTimeParam,
} from './podcastShow';
import {
  generatePodcastEpisodeSchema, generatePodcastBreadcrumbSchema,
} from './podcastSchema';
import PodcastPlayer from './PodcastPlayer';
import PodcastComments from './PodcastComments';
import './Podcast.css';

/**
 * PodcastEpisodePage — /podcast/<slug>/ (CEL-1355; design pass = Direction C
 * "The Interview", Nitish's pick shift 102).
 *
 * Layout: a sticky SPINE on the left (compact player + the chapter list as a
 * clickable timeline with progress fill and a you-are-here marker) beside the
 * main column (title → answerBlock → takeaway cards → the transcript typeset
 * as a two-voice DIALOGUE → comments → related). The spine keeps play/seek
 * and chapter nav on screen for the whole read — the primary interaction is
 * click-a-line-to-seek, so the player must never scroll away.
 *
 * Load-bearing rules (tutorials precedent, unchanged from the build pass):
 *  - Route validates against LIVE episodes only; unknown slug → hub.
 *  - answerBlock + takeaways + transcript are real static page text
 *    (prerendered by reactSnap for live episodes).
 *  - ?t= deep link parses to a first-play seek (preload=none respected).
 *  - PodcastEpisode @graph + BreadcrumbList JSON-LD; no transcript in JSON-LD.
 */
const PodcastEpisodePage = () => {
  const { episodeSlug } = useParams();
  const { search } = useLocation();
  const playerApi = useRef(null);
  const commentsApi = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [social, setSocial] = useState(null);

  const inShell = useInMarketingShell();
  const { isDarkMode } = useTheme();
  const dark = inShell ? true : isDarkMode;

  // Runtime registry (CEL-1362): the server only includes staged episodes for
  // admin sessions, so allowStaged is safe — ?preview=1 is the preview lane.
  const { episodes, show, loading } = usePodcastRegistry();
  const preview = new URLSearchParams(search).get('preview') === '1';
  const e = getEpisode(episodes, episodeSlug, { allowStaged: preview });

  // Hooks above any early return (rules-of-hooks); guarded internally.
  // Interleave chapter headings into the dialogue at their boundaries —
  // memoized on the episode so timeupdate rerenders don't rebuild the blocks.
  const { transcript, chapters, transcriptBlocks } = useMemo(() => {
    const tr = (e && e.transcript) || [];
    const ch = (e && e.chapters) || [];
    let nextChapterIdx = 0;
    const blocks = [];
    tr.forEach((row) => {
      while (
        nextChapterIdx < ch.length
        && ch[nextChapterIdx].startTime <= row.t
      ) {
        blocks.push({ kind: 'chapter', c: ch[nextChapterIdx] });
        nextChapterIdx += 1;
      }
      blocks.push({ kind: 'row', r: row });
    });
    return { transcript: tr, chapters: ch, transcriptBlocks: blocks };
  }, [e]);

  // While the registry loads, hold a blank page-colored frame (the SSR HTML
  // is what crawlers/users see first; this guards the client-side transition).
  if (loading && !e) return <div style={{ minHeight: '60vh' }} />;
  if (!e) return <Navigate to="/podcast" replace />;

  const startAt = parseTimeParam(new URLSearchParams(search).get('t'));
  const related = relatedEpisodes(episodes, e, 3);
  const pageUrl = `${SEO_DEFAULTS.url}/podcast/${e.slug}/`;

  let activeIdx = -1;
  transcript.forEach((row, i) => { if (currentTime >= row.t) activeIdx = i; });
  let rowIdx = -1;

  // Spine timeline state: chapters completed / current, and the fill height.
  let activeChapterIdx = -1;
  chapters.forEach((c, i) => { if (currentTime >= c.startTime) activeChapterIdx = i; });

  const rowSeek = (row) => {
    if (playerApi.current) playerApi.current.seekTo(row.t, { andPlay: true });
    trackEvent('podcast_transcript_seek', { episode: e.slug, t: row.t });
  };

  const chapterSeek = (c) => {
    if (playerApi.current) playerApi.current.seekTo(c.startTime, { andPlay: true });
    trackEvent('podcast_chapter_click', { episode: e.slug, chapter: c.title });
  };

  const hostName = show.host.name;

  return (
    <div className={`pod-page ${dark ? 'dark' : 'light'}${inShell ? ' in-shell' : ''}`}>
      <Helmet>
        <title>{e.seoTitle || e.title}</title>
        <meta name="description" content={e.description} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={e.seoTitle || e.title} />
        <meta property="og:description" content={e.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}${e.ogImage}`} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generatePodcastEpisodeSchema(e, show)} />
      <JsonLd data={generatePodcastBreadcrumbSchema(e)} />

      <div className="pod-wrap">
        <nav className="pod-crumb" aria-label="Breadcrumb">
          <Link to="/podcast">Podcast</Link>
          <span aria-hidden="true">/</span>
          <b aria-current="page">Episode {e.episodeNumber}</b>
        </nav>

        <div className="pod-epgrid">
          {/* The spine: player + chapter timeline, sticky through the read */}
          <aside className="pod-spine" aria-label="Player and chapters">
            <div className="pod-spine__now">
              <img className="pod-spine__art" src={e.artwork} alt="" width="46" height="46" />
              <div>
                <div className="pod-spine__ep">Episode {e.episodeNumber}</div>
                <div className="pod-spine__t">{e.title.split(' — ')[0]}</div>
              </div>
            </div>
            <PodcastPlayer
              variant="spine"
              src={e.audioUrl}
              durationSeconds={e.durationSeconds}
              chapters={chapters}
              slug={e.slug}
              startAt={startAt}
              apiRef={playerApi}
              onTimeUpdate={setCurrentTime}
            />
            {chapters.length > 0 && (
              <nav className="pod-tl" aria-label="Chapters">
                <div
                  className="pod-tl__fill"
                  style={{
                    height: activeChapterIdx > 0
                      ? `${(activeChapterIdx / chapters.length) * 100}%`
                      : 0,
                  }}
                  aria-hidden="true"
                />
                {chapters.map((c, i) => (
                  <button
                    key={c.startTime}
                    type="button"
                    className={`pod-tl__r${i === activeChapterIdx ? ' is-on' : ''}${i < activeChapterIdx ? ' is-done' : ''}`}
                    onClick={() => chapterSeek(c)}
                  >
                    <span className="pod-tl__t">{formatDuration(c.startTime)}</span>
                    {c.title}
                  </button>
                ))}
              </nav>
            )}
          </aside>

          {/* Main column */}
          <main>
            <header>
              <h1 className="pod-ep__t">{e.title.split(' — ')[0]}</h1>
              <p className="pod-byline">
                {/* External guest sites need a plain anchor (Router Link breaks on
                    absolute URLs) — the podcast's SEO give-back to guests. */}
                {e.guest.link
                  ? (e.guest.link.startsWith('http')
                    ? <a href={e.guest.link} target="_blank" rel="noopener noreferrer"><b>{e.guest.name}</b></a>
                    : <Link to={e.guest.link}><b>{e.guest.name}</b></Link>)
                  : <b>{e.guest.name}</b>}
                , {e.guest.role} — in conversation with{' '}
                <Link to={show.host.link}><b>{hostName}</b></Link>, {show.host.role}
              </p>
              <div className="pod-ep__meta">
                <span>{show.name}</span>
                <span className="pod-dot" aria-hidden="true">·</span>
                <span>Episode {e.episodeNumber}</span>
                <span className="pod-dot" aria-hidden="true">·</span>
                <span>{formatDuration(e.durationSeconds)}</span>
                {e.datePublished && (
                  <>
                    <span className="pod-dot" aria-hidden="true">·</span>
                    <span>
                      {new Date(e.datePublished).toLocaleDateString(undefined, {
                        month: 'long', day: 'numeric', year: 'numeric',
                      })}
                    </span>
                  </>
                )}
                {social && (
                  <>
                    <span className="pod-dot" aria-hidden="true">·</span>
                    <button
                      type="button"
                      className={`pod-meta-like${social.episode_liked_by_me ? ' is-on' : ''}`}
                      onClick={() => commentsApi.current && commentsApi.current.toggleEpisodeLike()}
                      aria-pressed={social.episode_liked_by_me}
                      aria-label="Like this episode"
                    >
                      ♥ {social.episode_likes}
                    </button>
                    <button
                      type="button"
                      className="pod-meta-cmts"
                      onClick={() => {
                        const el = document.getElementById('comments');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      {social.comment_count} comment{social.comment_count === 1 ? '' : 's'}
                    </button>
                  </>
                )}
              </div>
            </header>

            {/* answerBlock — the AEO lead paragraph */}
            <p className="pod-answer">{e.answerBlock}</p>

            {/* Takeaway cards */}
            <section className="pod-take" aria-label="Key takeaways">
              {e.takeaways.map((t, i) => (
                <div className="pod-take__i" key={t.slice(0, 40)}>
                  <b>Takeaway {i + 1}</b>
                  {t}
                </div>
              ))}
            </section>

            {/* The conversation — dialogue transcript, visible HTML at load (R5) */}
            {transcript.length > 0 && (
              <section aria-label="Transcript">
                <div className="pod-dlgh">
                  <h2 className="pod-h2">The conversation</h2>
                  <span>Click any line to listen from there</span>
                </div>
                {transcriptBlocks.map((b) => {
                  if (b.kind === 'chapter') {
                    return (
                      <h3 className="pod-mark" key={`c-${b.c.startTime}`}>
                        {b.c.title}
                      </h3>
                    );
                  }
                  rowIdx += 1;
                  const i = rowIdx;
                  const isHost = b.r.speaker === hostName;
                  return (
                    <button
                      type="button"
                      key={`r-${b.r.t}-${b.r.speaker}`}
                      className={`pod-turn ${isHost ? 'is-host' : 'is-guest'}${i === activeIdx ? ' is-on' : ''}`}
                      onClick={() => rowSeek(b.r)}
                      aria-label={`Jump to ${formatDuration(b.r.t)}`}
                    >
                      <span className="pod-turn__t">{formatDuration(b.r.t)}</span>
                      <span className="pod-turn__body">
                        <span className="pod-turn__s">{b.r.speaker}</span>
                        <span className="pod-turn__x">{b.r.text}</span>
                      </span>
                    </button>
                  );
                })}
              </section>
            )}

            {/* Comments (login-gated writes) */}
            <PodcastComments
              slug={e.slug}
              seedPrompt={e.seedPrompt}
              apiRef={commentsApi}
              onSocialChange={setSocial}
            />

            {/* Related episodes */}
            {related.length > 0 && (
              <section className="pod-related" aria-label="More episodes">
                <h2 className="pod-h2">More episodes</h2>
                {related.map((r) => (
                  <Link key={r.slug} to={`/podcast/${r.slug}/`} className="pod-entry pod-entry--sm">
                    <span className="pod-entry__fr">
                      <img src={r.artwork} alt="" width="72" height="72" loading="lazy" />
                    </span>
                    <span>
                      <span className="pod-entry__no">Episode {r.episodeNumber}</span>
                      <span className="pod-entry__t">{r.title.split(/ — |: with /)[0]}</span>
                    </span>
                    <span className="pod-entry__d"><b>{formatDuration(r.durationSeconds)}</b></span>
                  </Link>
                ))}
              </section>
            )}

            {(e.relatedBlogPosts || []).length > 0 && (
              <section className="pod-related" aria-label="Related reading">
                <h2 className="pod-h2">Related reading</h2>
                <ul className="pod-related__links">
                  {e.relatedBlogPosts.map((p) => (
                    <li key={p.url}><a href={p.url}>{p.title}</a></li>
                  ))}
                </ul>
              </section>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default PodcastEpisodePage;
